/**
 * launcherRegistry — the six AI task launchers offered from context menus and
 * toolbars across the app (Object Explorer, Schema Overview, Query Editor,
 * Performance Dashboard).
 *
 * Each launcher owns its prompt. The prompts are written out in full here, on
 * purpose: they are product copy, they are what the user's agent actually
 * receives, and they carry the one instruction that keeps the human in the loop
 * — **the agent never executes anything itself**. It writes SQL into the editor
 * (or answers in the chat) and the user runs it.
 *
 * `profile` describes what the task is *for*, not what the agent is allowed to
 * do: `write-confirm` tasks produce statements that will change data or schema
 * once a human runs them. It does not relax the CLI sandbox or the MCP
 * confirmation gate.
 *
 * The @-mention token (`@schema.name`) is the same one the chat composer
 * inserts (`AiChatInput.tsx`), so the agent resolves it against the schema
 * context it already has.
 */

export type AiTaskId =
  | 'generate-test-data'
  | 'explain-table'
  | 'document-schema'
  | 'optimize-query'
  | 'explain-error'
  | 'diagnose-server-load';

/** Where the launcher was fired from. Analytics dimension — keep it categorical. */
export type AiTaskSurface =
  | 'object-explorer'
  | 'schema-overview'
  | 'query-editor'
  | 'performance-dashboard'
  | 'unknown';

export type AiTaskContext = {
  connectionId: string;
  database?: string;
  object?: { schema?: string; name: string; kind: 'table' | 'view' };
  /** explain-error — the failure message the user just saw. */
  errorText?: string;
  /**
   * The query tab the launcher fired from, when the surface knows it (Query
   * Editor, error surfaces). Preferred as the host tab over the active tab, so
   * "Optimize this query" can never land on a sibling tab's buffer.
   */
  tabId?: string;
};

export type AiTaskLauncher = {
  id: AiTaskId;
  label: string;
  profile: 'read' | 'write-confirm';
  /**
   * Host-tab behaviour for this task (F0.4 SECOND AMENDMENT, fix-round-T13-r1
   * C-1). A property of the *task*, declared once here — not a decision each
   * call site is trusted to remember, because a per-call option is one a
   * future call site can forget.
   *
   * `'reuse'` — resolve an existing query tab via `findExistingQueryTab`
   * (preferring `ctx.tabId`, then the active tab, then first-match). Correct
   * for tasks fired *from* a query tab (`optimize-query`, `explain-error`
   * still pass `ctx.tabId`, which wins) or read-only tasks that answer in
   * chat (`explain-table`, `document-schema`).
   *
   * `'always-new'` — always `addTab`, skip the lookup entirely. Required for
   * tasks that write a substantial new artefact into the editor and have no
   * originating tab (`generate-test-data`, `diagnose-server-load`): the
   * first-match fallback these would otherwise hit can silently hijack a
   * stranger's unsaved query tab and full-replace it via `ui_set_editor_text`
   * / Monaco `setValue`, resetting the undo stack with no confirmation.
   *
   * Deliberately NOT derived from `profile` — `optimize-query` and
   * `explain-error` are both `profile: 'read'` yet both write the editor, so
   * `profile` does not discriminate "will this reuse a tab safely".
   */
  hostTabPolicy: 'reuse' | 'always-new';
  /**
   * Returns the full prompt, including @-mention tokens.
   *
   * A prompt is a function of the task and its `AiTaskContext`, nothing else.
   * There used to be a second `surface` parameter (G3 UX review F1) so one
   * task id could back two differently-worded buttons; `draft-migration` was
   * its only user and both of its buttons have since been withdrawn, so the
   * parameter went with it. `AiTaskSurface` survives purely as the analytics
   * dimension `openAiChatWithTask` reports — if a future task genuinely needs
   * different copy per surface, prefer two task ids over reviving per-surface
   * branching here, and put ordinary per-call data in `AiTaskContext`.
   */
  buildPrompt(ctx: AiTaskContext): string;
};

/** Declaration order — menus render in this order. */
export const AI_TASK_IDS = [
  'explain-table',
  'generate-test-data',
  'document-schema',
  'optimize-query',
  'explain-error',
  'diagnose-server-load',
] as const satisfies readonly AiTaskId[];

/** `@schema.name`, or `@name` on engines without schemas. */
function mention(ctx: AiTaskContext): string {
  const object = ctx.object;
  if (!object) return 'the selected object';
  return object.schema ? `@${object.schema}.${object.name}` : `@${object.name}`;
}

function objectNoun(ctx: AiTaskContext): string {
  return ctx.object?.kind === 'view' ? 'view' : 'table';
}

function databasePhrase(ctx: AiTaskContext): string {
  return ctx.database ? `the "${ctx.database}" database` : 'the connected database';
}

const NO_SELF_EXECUTION =
  'Do not execute anything yourself — the user reviews and runs it.';

export const launchers: Record<AiTaskId, AiTaskLauncher> = {
  'explain-table': {
    id: 'explain-table',
    label: 'Explain this table',
    profile: 'read',
    hostTabPolicy: 'reuse',
    buildPrompt: (ctx) =>
      [
        `Explain the ${objectNoun(ctx)} ${mention(ctx)} in ${databasePhrase(ctx)}.`,
        'Cover what it stores, what each column means, its keys, its foreign-key',
        'relationships, and how it is normally joined to the rest of the schema.',
        'Look its columns, indexes and constraints up with the schema tools first —',
        'do not guess names.',
        'Answer in the chat — do not run any queries.',
      ].join(' '),
  },

  'generate-test-data': {
    id: 'generate-test-data',
    label: 'Generate test data',
    profile: 'write-confirm',
    // C-1 retroactive fix (fix-round-T13-r1.md): fired from an Object Explorer
    // table with no originating query tab. Reusing whatever query tab merely
    // matches the connection risks INSERTs landing on a stranger's unsaved
    // buffer — always draft a fresh tab.
    hostTabPolicy: 'always-new',
    buildPrompt: (ctx) =>
      [
        `Generate realistic test data for ${mention(ctx)}.`,
        'Look its columns and foreign keys up with the schema tools first, then',
        'produce INSERT statements into the editor, target 25 rows, respect FK order.',
        'Match each column type, honour NOT NULL and unique constraints, and keep',
        'values plausible for what the column actually represents. Reading existing',
        'parent-key values with the schema/data tools to satisfy a foreign key is',
        'expected and fine — inventing one that does not exist will fail on insert.',
        NO_SELF_EXECUTION,
      ].join(' '),
  },

  'document-schema': {
    id: 'document-schema',
    label: 'Document this schema',
    profile: 'read',
    hostTabPolicy: 'reuse',
    buildPrompt: (ctx) =>
      [
        `Write documentation for the schema of ${databasePhrase(ctx)}.`,
        'Group the tables by the area of the product they serve, describe how they',
        'relate through foreign keys, name the entry-point tables someone new should',
        'start from, and call out anything that looks unused, duplicated, or ambiguous.',
        'Discover the tables and their relationships with the schema tools — do not',
        'guess names.',
        'Write the documentation in the chat as Markdown — do not run any queries.',
      ].join(' '),
  },

  'optimize-query': {
    id: 'optimize-query',
    label: 'Optimize this query',
    profile: 'read',
    // Fires from a query tab whose id the call site always knows — reuse it
    // via ctx.tabId, which findExistingQueryTab prefers over the active tab.
    hostTabPolicy: 'reuse',
    buildPrompt: (ctx) =>
      [
        `Optimize the SQL currently in the editor (${databasePhrase(ctx)}).`,
        'Explain what makes it slow, rewrite it, and list the indexes that would help',
        'and why. Check the rewrite returns the same rows as the original — say so',
        'explicitly if it cannot.',
        'Put the rewritten SQL in the editor and keep the original in a comment.',
        'Do not run anything — the user executes and compares.',
      ].join(' '),
  },

  'explain-error': {
    id: 'explain-error',
    label: 'Explain this error',
    profile: 'read',
    // Same reasoning as optimize-query — the call site knows its own tabId.
    hostTabPolicy: 'reuse',
    buildPrompt: (ctx) =>
      [
        `A statement in the editor failed against ${databasePhrase(ctx)} with this error:`,
        '',
        // F3 (G3 UX review): unreachable today (MessagesPanel always passes
        // errorText) — but a fabricated "you can see it" line is a
        // confident-fabrication seed the moment a future call site omits the
        // field. State the absence instead of claiming visibility the agent
        // does not have.
        ctx.errorText?.trim()
          ? ctx.errorText.trim()
          : '(no error text was provided — ask the user to paste the error message)',
        '',
        'Explain what the error means, why this statement triggered it, and how to fix',
        'it. If the fix is a change to the SQL, put the corrected statement in the',
        'editor. Do not run anything yourself.',
      ].join('\n'),
  },

  'diagnose-server-load': {
    id: 'diagnose-server-load',
    label: 'Diagnose server load',
    profile: 'read',
    // T17b (D-4a). Fires from the Performance Dashboard toolbar, a surface
    // with no query tab of its own, and the prompt ends with "put any
    // suggested fix script in the editor" — a write. The always-new/reuse
    // split is a property of the *task*, not of `profile` ('read' here, same
    // as optimize-query/explain-error, which both reuse — see the field's
    // doc comment above): reusing whatever tab merely matches the connection
    // would risk silently full-replacing a stranger's unsaved query buffer
    // via ui_set_editor_text / Monaco setValue. Always draft a fresh tab.
    hostTabPolicy: 'always-new',
    // Deliberately engine-neutral and static — the agent gathers its own
    // data through the jam-sql tools (top CPU queries, wait stats,
    // long-running sessions), so this prompt sends no result rows or metrics
    // of its own to pre-fetch (same invariant as buildDataCompareDiffSummary:
    // identifiers and change kinds, never cell/metric values — this task
    // sends neither).
    buildPrompt: () =>
      "I'm seeing CPU and memory spikes on this server. Using the jam-sql tools (read-only), find what's causing them: check the top queries by total CPU across all databases, current wait statistics, and long-running sessions — then tell me which database and which query to fix first, and why. Put any suggested fix script in the editor; do not execute anything yourself.",
  },
};

/** Menu-ready list in declaration order. */
export const launcherList: AiTaskLauncher[] = AI_TASK_IDS.map((id) => launchers[id]);
